import { useCallback } from 'react';

import { useLocation } from 'react-router';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	useStarterDashboardBannerState,
	useStarterDashboardPageData,
} from '#src/app/pages/starterDashboard/pageContext';
import { useCurrentAuthenticatedUser } from '#src/app/shared/hooks/session';

import { useVanityLinkModal } from './useVanityLinkModal';

export function useTriggerVanityLinkModal(): () => Promise<void> {
	const {
		data: { petition },
		actions: { refreshData },
	} = useStarterDashboardPageData();
	const { actions } = useStarterDashboardBannerState();
	const { translate } = useI18n();
	const track = useTracking();

	const openModal = useVanityLinkModal();

	const user = useCurrentAuthenticatedUser();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const triggerVanityLinkModal = useCallback(async () => {
		void track('custom_url_create_click', { num_signatures: petition.signatureState.signatureCount.displayed });

		if (!user) {
			const params = encodeURIComponent(`${pathname}${search}`);
			navigate(`/login_or_join?redirectTo=${params}`);
			return;
		}

		const result = await openModal({
			petitionSlug: petition.slug,
			petitionId: petition.id,
			displayedSignatures: petition.signatureState.signatureCount.displayed,
		}).waitForResult();

		if (result?.status === 'success') {
			void track('custom_url_success', { num_signatures: petition.signatureState.signatureCount.displayed });
			actions.addBanner({
				message: translate('corgi.petition.starter_dashboard.grow_petition.copy_link.confirm_link_success'),
				severity: 'success',
				dismissable: true,
			});
			refreshData();
			return;
		}

		void track('custom_url_create_cancel', { num_signatures: petition.signatureState.signatureCount.displayed });
	}, [openModal, actions, track, navigate, pathname, petition, search, translate, user, refreshData]);

	return triggerVanityLinkModal;
}
